import React, { useState } from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, Star, Send, CheckCircle, Clock, MessageSquare, Loader2 } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { contactInfo, portfolioImages } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '0b02448c-3329-4d04-ba3d-656625a4a6dc',
          subject: `New Inquiry from ${formData.name} - ${formData.projectType || 'General'}`,
          from_name: 'InArch Design Studio Website',
          to: 'info@inarchdesignstudio.com',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: formData.projectType,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        setError('Failed to send message. Please try again or contact us directly.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    'Residential Interior',
    'Commercial Interior',
    'Landscape',
    'Villas & Resorts',
    'Kitchen Design',
    'Office Design',
    'Other'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-[#2C2C2C] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={portfolioImages.hero[2]}
            alt="Contact Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C2C2C] via-[#2C2C2C]/95 to-[#2C2C2C]/80" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 animate-fade-in">
              Get in Touch
            </span>
            <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white mb-6 leading-tight animate-fade-in-up">
              Ready to Transform
              <span className="block text-[#D4956A]">Your Space?</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed animate-fade-in-up delay-200">
              Let's design something extraordinary together. Reach out to us and 
              take the first step towards your dream interior.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 lg:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Information */}
            <div>
              <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
                Contact Information
              </span>
              <h2 className="font-serif text-4xl text-[#2C2C2C] mb-6">
                Let's Start a
                <span className="block text-[#B87333]">Conversation</span>
              </h2>
              <div className="divider-copper mb-8" />
              <p className="text-[#6B6B6B] text-lg leading-relaxed mb-10">
                Whether you have a clear vision or just a spark of an idea, we're 
                here to listen, guide, and create. Every great space begins with 
                a conversation.
              </p>

              {/* Contact Details */}
              <div className="space-y-6 mb-10">
                <div className="flex items-start p-6 bg-[#F5F0E8] border border-[#E8E3DB] hover:border-[#B87333]/30 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mr-5 flex-shrink-0">
                    <MapPin size={22} className="text-[#B87333]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C2C2C] mb-2">Visit Our Office</h3>
                    <div className="text-[#6B6B6B] text-sm leading-relaxed">
                      <p>{contactInfo.address.line1}</p>
                      <p>{contactInfo.address.line2}</p>
                      <p>{contactInfo.address.line3}</p>
                      <p>{contactInfo.address.line4}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start p-6 bg-[#F5F0E8] border border-[#E8E3DB] hover:border-[#B87333]/30 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mr-5 flex-shrink-0">
                    <Phone size={22} className="text-[#B87333]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C2C2C] mb-2">Call Us</h3>
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="block text-[#6B6B6B] hover:text-[#B87333] transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                    <a
                      href={`tel:${contactInfo.phone2}`}
                      className="block text-[#6B6B6B] hover:text-[#B87333] transition-colors mt-1"
                    >
                      {contactInfo.phone2}
                    </a>
                  </div>
                </div>

                <div className="flex items-start p-6 bg-[#F5F0E8] border border-[#E8E3DB] hover:border-[#B87333]/30 transition-colors">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mr-5 flex-shrink-0">
                    <Mail size={22} className="text-[#B87333]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#2C2C2C] mb-2">Email Us</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-[#6B6B6B] hover:text-[#B87333] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-serif text-lg text-[#2C2C2C] mb-4">Follow Us</h3>
                <div className="flex items-center space-x-4">
                  <a
                    href={contactInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-3 border border-[#E8E3DB] hover:border-[#B87333] hover:bg-[#B87333]/5 transition-all duration-300 group"
                  >
                    <Instagram size={18} className="text-[#6B6B6B] group-hover:text-[#B87333] transition-colors" />
                    <span className="ml-2 text-sm text-[#6B6B6B] group-hover:text-[#B87333] transition-colors">Instagram</span>
                  </a>
                  <a
                    href={contactInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-3 border border-[#E8E3DB] hover:border-[#B87333] hover:bg-[#B87333]/5 transition-all duration-300 group"
                  >
                    <Facebook size={18} className="text-[#6B6B6B] group-hover:text-[#B87333] transition-colors" />
                    <span className="ml-2 text-sm text-[#6B6B6B] group-hover:text-[#B87333] transition-colors">Facebook</span>
                  </a>
                  <a
                    href={contactInfo.social.google}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-4 py-3 border border-[#E8E3DB] hover:border-[#B87333] hover:bg-[#B87333]/5 transition-all duration-300 group"
                  >
                    <Star size={18} className="text-[#6B6B6B] group-hover:text-[#B87333] transition-colors" />
                    <span className="ml-2 text-sm text-[#6B6B6B] group-hover:text-[#B87333] transition-colors">Reviews</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white p-8 lg:p-10 border border-[#E8E3DB] shadow-sm">
                <div className="flex items-center mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mr-4">
                    <MessageSquare size={18} className="text-[#B87333]" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl text-[#2C2C2C]">Send Us a Message</h3>
                    <p className="text-[#6B6B6B] text-sm">We'll respond within 24 hours</p>
                  </div>
                </div>

                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center bg-green-100 rounded-full mb-6">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="font-serif text-2xl text-[#2C2C2C] mb-3">Thank You!</h3>
                    <p className="text-[#6B6B6B]">
                      Your message has been received. We'll get back to you shortly.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-[#2C2C2C]">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="h-12 border-[#E8E3DB] focus:border-[#B87333] focus:ring-[#B87333] bg-[#FDFBF7]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-[#2C2C2C]">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="h-12 border-[#E8E3DB] focus:border-[#B87333] focus:ring-[#B87333] bg-[#FDFBF7]"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-[#2C2C2C]">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 98765 43210"
                          className="h-12 border-[#E8E3DB] focus:border-[#B87333] focus:ring-[#B87333] bg-[#FDFBF7]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="projectType" className="text-[#2C2C2C]">Project Type</Label>
                        <select
                          id="projectType"
                          name="projectType"
                          value={formData.projectType}
                          onChange={handleChange}
                          className="w-full h-12 px-3 border border-[#E8E3DB] focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] bg-[#FDFBF7] text-[#2C2C2C] rounded-md"
                        >
                          <option value="">Select a type</option>
                          {projectTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-[#2C2C2C]">Your Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell us about your project, your vision, and how we can help..."
                        className="border-[#E8E3DB] focus:border-[#B87333] focus:ring-[#B87333] bg-[#FDFBF7] resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full h-14 bg-[#B87333] hover:bg-[#8B5A2B] text-white text-sm tracking-wider uppercase transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <Loader2 size={18} className="mr-2 animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center justify-center">
                          <Send size={18} className="mr-2" />
                          Send Message
                        </span>
                      )}
                    </Button>

                    {error && (
                      <p className="text-red-500 text-sm text-center">{error}</p>
                    )}

                    <p className="text-[#9A9A9A] text-xs text-center">
                      By submitting this form, you agree to be contacted regarding your inquiry.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-16 bg-[#F5F0E8] border-t border-[#E8E3DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mb-4">
                <Clock size={24} className="text-[#B87333]" />
              </div>
              <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">Response Time</h3>
              <p className="text-[#6B6B6B] text-sm">We respond to all inquiries within 24 hours</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mb-4">
                <MessageSquare size={24} className="text-[#B87333]" />
              </div>
              <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">Free Consultation</h3>
              <p className="text-[#6B6B6B] text-sm">Initial design consultations are complimentary</p>
            </div>
            <div className="text-center p-6">
              <div className="w-14 h-14 mx-auto flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mb-4">
                <MapPin size={24} className="text-[#B87333]" />
              </div>
              <h3 className="font-serif text-xl text-[#2C2C2C] mb-2">Office Visits</h3>
              <p className="text-[#6B6B6B] text-sm">Schedule a visit to see our work up close</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
